/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate } from "react-router-dom";
import {
  distributeObjects,
  dynamicTileCodeInterface,
  hasCommonElements,
} from "../routing/FunctionsAndInterfaces";
import { useLazyGetPillsByWorkspaceQuery } from "../services/gallusTools";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useValidatedUserPayload } from "../app/hooks";

export interface ButtonAndDescription {
  title: string;
  titleSettings?: TitleSettings;
  backgroundImageName?: string;
  headOfSubcategory?: boolean;
  link?: string;
  description?: string;
  internal?: boolean;
  overrideParentURL?: boolean;
  onlyShowForCognitoGroups?: string[];
}

interface TitleSettings {
  isTitle: boolean;
  isBigTitle: boolean;
}

interface Extra {
  internal: boolean;
  title: string;
  link: string;
}

interface TSLandingPageProps {
  /**
   * parentURL is the "suffix" of the link
   */
  parentURL: string;
  /**
   * each ButtonAndDescription[] array is a column, each ButtonAndDescription, a button
   */
  buttons?: ButtonAndDescription[][];
  dynamicButtonsCode?: dynamicTileCodeInterface;
  smallButtons: boolean;
  extraButton?: Extra;
}

export const TSLandingPage = (props: TSLandingPageProps) => {
  const navigate = useNavigate();
  const [getTilesQuery, getTilesQueryResult] =
    useLazyGetPillsByWorkspaceQuery();
  const [buttonSet, setButtonSet] = useState<JSX.Element>(<></>);
  const [loading, setLoading] = useState<boolean>(true);
  const userSession = useValidatedUserPayload();

  useEffect(() => {
    const makeButtonSet = async () => {
      setLoading(true);
      let buttonSet: JSX.Element[] = [];
      if (props.buttons) {
        props.buttons.forEach((element) => {
          buttonSet.push(makeButtonColumn(element));
        });
      }
      if (props.dynamicButtonsCode) {
        try {
          const tiles = await getTilesQuery({
            workspace: props.dynamicButtonsCode.code,
          }).unwrap();
          var distributedTiles: ButtonAndDescription[][] = [];
          if (tiles.length === 0 && props.dynamicButtonsCode.backup) {
            //Redundant check. Some firewalls block the call but it doesn't register it as a failed call.
            distributedTiles = distributeObjects(
              props.dynamicButtonsCode.backup,
              3,
            );
          } else {
            distributedTiles = distributeObjects(tiles, 3);
          }
          distributedTiles.forEach((element) => {
            buttonSet.push(makeButtonColumn(element));
          });
        } catch {
          if (props.dynamicButtonsCode.backup) {
            distributeObjects(props.dynamicButtonsCode.backup, 3).forEach(
              (element) => {
                buttonSet.push(makeButtonColumn(element));
              },
            );
          }
        }
      }
      setButtonSet(<div className="responsive-centered-row">{buttonSet}</div>);
      setLoading(false);
    };
    if (userSession && userSession.cognitoGroups) {
      makeButtonSet();
    }
  }, [props, userSession]);

  const makeButtonColumn = (buttonColumn: ButtonAndDescription[]) => {
    let column: JSX.Element[] = [];

    let size: string = props.smallButtons
      ? "lp-pill-smallest"
      : "lp-pill-small";
    buttonColumn.forEach((element) => {
      if (element.titleSettings?.isTitle) {
        let fontSize: string = element.titleSettings.isBigTitle
          ? "lp-title"
          : "lp-title-small";
        column.push(
          <div className={fontSize} key={element.title}>
            {element.title}
          </div>,
        );
      } else {
        if (
          userSession &&
          userSession.cognitoGroups &&
          (!element.onlyShowForCognitoGroups ||
            (element.onlyShowForCognitoGroups &&
              (hasCommonElements(
                userSession.cognitoGroups,
                element.onlyShowForCognitoGroups,
              ) ||
                userSession.cognitoGroups.includes("Administrator"))))
        ) {
          column.push(
            <div key={element.title}>
              <div
                style={{
                  display: element.headOfSubcategory ? "none" : "block",
                }}
              >
                <div
                  className="lp-title-small"
                  key={element.title}
                  style={{ visibility: "hidden" }}
                >
                  -
                </div>
              </div>
              <div className={size}>
                <div>
                  <div className="lp-pill-name">{element.title}</div>
                  <div className="lp-pill-description">
                    {element.description}
                  </div>
                </div>
                <div>
                  <button
                    type="button"
                    className="btn btn-primary lp-pill-button"
                    onClick={(e) => {
                      e.preventDefault();
                      if (element.internal === true) {
                        if (element.overrideParentURL === true) {
                          navigate(element.link!);
                        } else {
                          navigate(props.parentURL + "/" + element.link);
                        }
                      } else {
                        navigate(
                          props.parentURL + "?productURL=" + element.link,
                        );
                      }
                    }}
                  >
                    Launch
                  </button>
                </div>
                <div
                  className="lp-pill-background lp-pill-low-opacity"
                  style={{
                    backgroundImage: "url(" + element.backgroundImageName + ")",
                  }}
                ></div>
              </div>
            </div>,
          );
        }
      }
    });

    return <div className="d-flex flex-column">{column}</div>;
  };

  const makeExtraButton = () => {
    let button: JSX.Element = <div></div>;

    if (props.extraButton) {
      button = (
        <div>
          <button
            type="button"
            className="lp-extra-button"
            onClick={(e) => {
              e.preventDefault();
              if (props.extraButton!.internal) {
                navigate(props.extraButton!.link);
              } else {
                navigate(
                  props.parentURL + "?productURL=" + props.extraButton!.link,
                );
              }
            }}
          >
            {props.extraButton!.title}
          </button>
        </div>
      );
    }

    return button;
  };

  return (
    <div>
      {loading && (
        <div className="loading-div">
          <FontAwesomeIcon
            icon={faSpinner}
            size="2x"
            spin={true}
            style={{ margin: "0 auto" }}
          />
        </div>
      )}
      {!loading && (
        <div>
          <div style={{ width: "98%", marginLeft: "auto" }}>
            {makeExtraButton()}
          </div>
          <div className="d-flex justify-content-center">{buttonSet}</div>
        </div>
      )}
    </div>
  );
};
